import * as faker from 'faker/locale/cz'
import {action, observable} from 'mobx'
import {isEmpty} from '../../utils/object.util'
import {HttpService} from '../http/http.service'
import {IImage} from './image.interface'
import {DefaultOrder, ISearchParameters} from './store.types'

export class Item {
  @observable changedProps: any = {}
  @observable id: string
  _id: string

  constructor(model?: Item) {
    if (model) {
      this.update(model, false)
      this.id = model.id || model._id
    }
  }

  @action update(props: any, passToChangedProps: boolean = true) {
    for (const prop in props) {
      if (props.hasOwnProperty(prop)) {
        this[prop] = props[prop]
        if (passToChangedProps) {
          this.changedProps[prop] = props[prop]
        }
      }
    }
  }
}

export abstract class Store<T extends Item> {
  @observable items: T[] = []
  @observable item: T = null
  @observable changedItem: T = null
  @observable itemChanged: boolean = false
  @observable count: number = 0
  @observable pending: boolean = false
  @observable changedTabs: string[] = []
  protected resource: string
  private itemConstructor: any
  private fakerDefinition = null
  private fakerObjectFn: () => T = null
  private lastSearchParameters: ISearchParameters = null
  protected modifyFilter?: (filter: any) => any = null
  defaultOrder: DefaultOrder = null
  lastResponse: any = {}
  lastActiveState: any = {}
  @observable gridChanged: boolean = false;

  protected constructor(protected http: HttpService) {
    this.setDefaults()
  }

  @action async fetchItems(params?: ISearchParameters, customUrl?: string): Promise<{records: T[], count: number}> {
    let urlParams = {
      page: 0,
      perPage: 50,
    }

    if (params && isEmpty(params.filters) && !params.order && this.lastSearchParameters) {
      params = {...params, ...this.lastSearchParameters.order, ...this.lastSearchParameters.filters}
    }

    if (params) {
      urlParams = {
        page: params.pagination ? params.pagination.page : 0,
        perPage: params.pagination ? params.pagination.perPage : 50,
      }
      if (params.order) {
        urlParams = {...urlParams, ...params.order}
      }

      Object.keys(params.filters).map(key => {
        const filter = params.filters[key]
        if (key !== 'term') {
          urlParams = {...urlParams, [key]: filter}
        }
      })

      this.lastSearchParameters = params
    }
    let urlParamsArray: string[] = []
    Object.keys(urlParams).map(key => {
      urlParamsArray.push(`${key}=${urlParams[key]}`)
    })

    this.setPending(true)
    const apiResponse = await this.http.get(customUrl ? customUrl : `${this.resource}?${urlParamsArray.join('&')}`) as {records: T[], count: number}
    this.refreshItems(apiResponse.records)
    this.setPending(false)
    this.lastResponse = {...apiResponse}
    delete this.lastResponse.records
    return {records: this.items, count: apiResponse.count}
  }

  @action async fetchItem(id: string): Promise<T> {
    this.setPending(true)
    this.item = null
    const record = await this.http.get(`${this.resource}/${id}`)
    this.changedTabs = []
    if (record) {
      this.item = this.getNew(record)
      this.changedItem = this.getNew(record)
    } else {
      this.item = this.getNew(record)
      this.changedItem = this.getNew(record)
    }
    this.setPending(false)
    return this.item
  }

  @action async fetchMe(): Promise<T> {
    this.setPending(true)
    const record = await this.http.get('/user/me')
    this.setPending(false)
    return record
  }

  @action async fetchItemsList(): Promise<T[]> {
    this.setPending(true)
    const apiResponse = await this.http.get(`${this.resource}/search`)
    this.refreshItems(apiResponse.records)
    this.setPending(false)
    return this.items
  }

  @action async fetchList(page: number, perPage: number, filter: any = {}): Promise<any> {
    this.setPending(true)
    const apiResponse = await this.http.get(`${this.resource}?page=${page}&perPage=${perPage}&filter=${encodeURIComponent(JSON.stringify(filter))}`)
    this.setPending(false)
    return apiResponse
  }

  @action async createItem(props: any): Promise<T> {
    if (!props) {
      return
    }

    this.setPending(true)
    try {
      const {record} = await this.http.post(`${this.resource}`, props)
      const item = this.getNew(record)
      this.items = [item, ...this.items]
      this.setPending(false)
      return item
    } catch (e) {
      this.setPending(false)
      throw this.processErrors(e)
    }
  }

  @action async updateItem(id: string, props: any, indicatePending: boolean = true, updateItem: boolean = true): Promise<T> {
    if (indicatePending) {
      this.setPending(true)
    }

    try {
      const record = await this.http.put(`${this.resource}/${id}`, props)
      const item = this.getNew(record)
      if (updateItem) {
        this.item = item
        const i = this.items.find(i => i.id === item.id)
        if (i && i.update) {
          i.update(item)
        }
      }
      this.changedItem = this.getNew(item)
      this.changedTabs = []
      this.setItemChanged(false)
      this.setPending(false)
      return item
    } catch (e) {
      this.setPending(false)
      throw this.processErrors(e)
    }
  }

  @action async removeItem(id: string) {
    this.setPending(true)
    try {
      await this.http.delete(`${this.resource}/${id}`)
      this.items = this.items.filter(i => i.id !== id)
      this.setPending(false)
      return
    } catch (e) {
      this.setPending(false)
      throw this.processErrors(e)
    }
  }

  @action async search(filter: any, page: number = 0, perPage: number = 20): Promise<{items: T[], count: number}> {
    try {
      this.setPending(true)
      filter.page = page
      filter.perPage = perPage
      const {items, count} = await this.http.post(`${this.resource}/search`, filter)
      this.setPending(false)
      return {items, count}
    } catch (e) {
      this.setPending(false)
      throw e
    }
  }

  abstract setDefaults()

  setResource(resource: string) {
    this.resource = resource
  }

  setItemConstructor(constructor: any) {
    this.itemConstructor = constructor
  }

  setModifyFilter(fn: (any: any) => T) {
    this.modifyFilter = fn
  }


  setDefaultOrder(order: DefaultOrder) {
    this.defaultOrder = order
  }

  setFakerObject(fn: () => T) {
    this.fakerObjectFn = fn
  }

  setFakerDefinition(def: any) {
    this.fakerDefinition = def
  }

  get(id: string): T {
    return this.items.find(i => i.id === id)
  }

  copy(id: string): T {
    return this.getNew(this.get(id))
  }

  getNew(item: T = null): T {
    return new this.itemConstructor(item)
  }

  getImageForAPI(image: IImage) {
    if (image) {
      if (image.removed) { return null }
      if (!image.changed) { return undefined }
      delete image.removed
      delete image.changed
      delete image.location
      delete image.id
      return image
    }
    return undefined
  }

  @action destroy(): void {
    this.items = []
  }

  @action setItemChanged(val: boolean) {
    this.itemChanged = val
  }

  @action addChangedTab(routePath: string) {
    this.changedTabs.push(routePath)
  }

  @action removeChangedTab(routePath: string) {
    this.changedTabs = this.changedTabs.filter(i => i !== routePath)
  }

  isTabChanged(routePath: string): boolean {
    return this.changedTabs.includes(routePath)
  }

  @action protected refreshItems(items: T[]) {
    this.items = items.map(i => this.getNew(i))
    this.count = items.reduce(total => ++total, 0)
  }

  @action protected setPending(pending: boolean) {
    this.pending = pending
  }

  protected generateFakeItem(count: number = 1): T[] {
    const items = []
    for (let i = 0; i < count; i++) {
      if (this.fakerObjectFn) {
        items.push(this.getNew(this.fakerObjectFn()))
      } else {
        const item = this.getNew()
        for (const prop in this.fakerDefinition) {
          if (this.fakerDefinition.hasOwnProperty(prop)) {
            item[prop] = faker.fake('{{' + this.fakerDefinition[prop] + '}}')
          }
        }
        items.push(item)
      }
    }
    return items
  }

  protected processErrors(e: any): string[] {
    let errs = []

    if (e.error) {
      if (e.error.errors) {
        errs = e.error.errors.map(i => i.message)
      }
    }

    return errs
  }

  setLastActiveState(activeState: any) {
    this.lastActiveState = activeState
  }

  getLastActiveState() {
    return this.lastActiveState
  }

  @action setGridChanged(val: boolean) {
    this.gridChanged = val
  }

  private fixBooleanFilters(filters: any) {
    Object.keys(filters).map(key => {
      if (filters[key] === 'true') {
        filters[key] = true
      }

      if (filters[key] === 'false') {
        filters[key] = false
      }
    })
  }
}
